import { useEffect, useState } from 'react';
import { useLocale } from '../../application/i18n/LocaleProvider';
import { hasSeenDeckHint, rememberDeckHintSeen } from '../../application/plants/deckHint';
import { BackIcon, ForwardIcon } from '../icons/ControlIcons';

/**
 * The one time this app says the deck can be swiped (SPEC-025 D-18).
 *
 * Drawn over the picture, because that is where the gesture is made and a hint
 * printed somewhere else teaches the wrong place. It waits for the card to finish
 * introducing its plant — a hint that arrives during the reveal is one more thing
 * moving — and then leaves on its own, or the moment the reader swipes.
 *
 * `aria-hidden`: it describes a gesture nobody reading by ear can make, and the
 * deck's own controls (D-10) are that reader's path. Transparent to pointers, so
 * the drag it is asking for lands on the card underneath it rather than on the
 * words about it.
 */

/** After the reveal has finished (D-14), so it is the only thing moving. */
const APPEARS_AFTER_MS = 1500;

/** Long enough to read twice, short enough not to become furniture. */
const STAYS_FOR_MS = 4600;

export default function PlantDeckHint({ dismissed }) {
    const { t } = useLocale();
    // Read once, on mount, rather than on every render: the answer changes while
    // this component is alive and re-reading it would hide the hint mid-show.
    const [isOffered] = useState(() => !hasSeenDeckHint());
    const [isShowing, setShowing] = useState(false);

    useEffect(() => {
        if (!isOffered) {
            return undefined;
        }

        const shown = window.setTimeout(() => setShowing(true), APPEARS_AFTER_MS);
        const gone = window.setTimeout(() => {
            setShowing(false);
            rememberDeckHintSeen();
        }, APPEARS_AFTER_MS + STAYS_FOR_MS);

        return () => {
            window.clearTimeout(shown);
            window.clearTimeout(gone);
        };
    }, [isOffered]);

    // The reader has done the thing. Nothing left to teach, and it is remembered
    // even if they never waited for the hint to finish.
    useEffect(() => {
        if (isOffered && dismissed) {
            setShowing(false);
            rememberDeckHintSeen();
        }
    }, [dismissed, isOffered]);

    if (!isOffered || !isShowing) {
        return null;
    }

    return (
        <p className="deck-hint" aria-hidden="true">
            <BackIcon size={20} />
            <span className="deck-hint__words">{t('plants.detail.swipeHint')}</span>
            <ForwardIcon size={20} />
        </p>
    );
}
